/**
 * Single source of truth for booking fees.
 *
 * Values live ONLY in the environment (`.env` / deployment secrets) — never hardcode
 * a price in code. `getFee()` throws if a fee is missing or invalid, so a misconfigured
 * deploy fails loudly (see instrumentation.ts, which checks these at server boot) rather
 * than silently charging a stale or zero amount at checkout.
 *
 * Env reads use static keys (not `process.env[dynamic]`) so they resolve correctly in
 * every bundling context.
 */

export type FeeKey =
  | "consultation"
  | "childConsultation"
  | "flexible"
  | "bodhiClub"
  | "farleighMethod"
  | "retreatDeposit";

export const FEE_ENV_VARS: Record<FeeKey, string> = {
  consultation: "NEXT_PUBLIC_CONSULTATION_FEE",
  childConsultation: "NEXT_PUBLIC_CHILD_CONSULTATION_FEE",
  flexible: "NEXT_PUBLIC_FLEXIBLE_FEE",
  bodhiClub: "NEXT_PUBLIC_BODHI_CLUB_FEE",
  farleighMethod: "NEXT_PUBLIC_FARLEIGH_METHOD_FEE",
  retreatDeposit: "NEXT_PUBLIC_RETREAT_DEPOSIT",
};

function readRaw(key: FeeKey): string | undefined {
  switch (key) {
    case "consultation": return process.env.NEXT_PUBLIC_CONSULTATION_FEE;
    case "childConsultation": return process.env.NEXT_PUBLIC_CHILD_CONSULTATION_FEE;
    case "flexible": return process.env.NEXT_PUBLIC_FLEXIBLE_FEE;
    case "bodhiClub": return process.env.NEXT_PUBLIC_BODHI_CLUB_FEE;
    case "farleighMethod": return process.env.NEXT_PUBLIC_FARLEIGH_METHOD_FEE;
    case "retreatDeposit": return process.env.NEXT_PUBLIC_RETREAT_DEPOSIT;
  }
}

/** Parsed fee, or null if the env var is missing / non-numeric / not positive. */
export function getFeeOptional(key: FeeKey): number | null {
  const raw = readRaw(key);
  const n = Number(raw);
  return raw !== undefined && raw !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/** Parsed fee. Throws if the env var is missing or invalid — callers on the charge path use this. */
export function getFee(key: FeeKey): number {
  const fee = getFeeOptional(key);
  if (fee === null) {
    throw new Error(
      `Fee "${key}" is not configured — set ${FEE_ENV_VARS[key]} in the environment (positive number).`
    );
  }
  return fee;
}

/**
 * Display price, e.g. "£250" (or "£12.50" where the fee has pence).
 *
 * Marketing copy uses this rather than `getFee()` so a missing env var never throws
 * mid-render and takes a whole page down — the boot check in instrumentation.ts and
 * the 503 on the charge path are where a misconfigured fee is meant to surface.
 * The empty string is deliberately conspicuous: a price that reads "Adults  ·
 * Children " is obviously broken, where a hardcoded fallback would quietly look fine
 * while the customer is charged something else. That divergence is exactly what this
 * module exists to prevent, so there is no fallback.
 */
export function feeLabel(key: FeeKey): string {
  const fee = getFeeOptional(key);
  if (fee === null) {
    console.error(
      `[fees] ${FEE_ENV_VARS[key]} is not configured — price omitted from display copy.`
    );
    return "";
  }
  return `£${Number.isInteger(fee) ? fee : fee.toFixed(2)}`;
}

/** Returns the names of any fee env vars that are missing or invalid. Used by the startup check. */
export function missingFeeVars(): string[] {
  return (Object.keys(FEE_ENV_VARS) as FeeKey[])
    .filter((key) => getFeeOptional(key) === null)
    .map((key) => FEE_ENV_VARS[key]);
}
